import React from "react";
import { useHistory } from "react-router-dom";
import {
    Box,
    Dialog,
    IconButton,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableRow,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import Diversity3RoundedIcon from "@mui/icons-material/Diversity3Rounded";
import CelebrationRoundedIcon from "@mui/icons-material/CelebrationRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import FeedbackRoundedIcon from "@mui/icons-material/FeedbackRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";

import { ds } from "../../styles/designSystem";
import PushSetupModal from "../../page/push/PushSetupModal";
import { isAndroid, isIOS, isStandalone } from "../../util/push/browserSupport";
/* 홈(DashboardPage)과 같은 현황을 말해야 하므로 목록은 한 파일에서만 관리한다. */
import { FEEDBACK_DONE, FEEDBACK_PENDING, MAIL_RESTORE_NOTICE } from "../../config/feedbackStatus";
import ActionButton from "./ActionButton";

/**
 * 로그인 직후 1회 표시되는 시스템 안내(공지) 모달.
 *
 * <p>매 로그인마다 뜨되, 한 번의 로그인 세션에서는 딱 한 번만 표시된다
 * (표시 트리거는 App.js 의 onLoginSuccess — 새로고침/재방문으로는 재표시되지 않음).
 *
 * 구성:
 *  1) 인사말
 *  2) 직원분들이 주신 피드백의 반영 현황 (완료 / 진행 중·예정) — 피드백 게시판 상태와 같은 목록
 *  3) 전체 기능 표 (사이드바 기준으로 스캔한 사내 시스템 기능 개요)
 *  4) 요청·건의는 좌측 '피드백' 메뉴로 유도
 */

/* ─── 기능 개요 데이터 (사이드바 그룹 기준) ─────────────────────
   ★ 사이드바(OnandmeSidebar.buildMenu) 의 그룹·항목과 1:1 로 맞춘다.
     메뉴가 늘거나 이름이 바뀌면 여기도 함께 갱신할 것. */
const FEATURE_GROUPS = [
    {
        group: "사내 운영",
        items: [
            { name: "채널", desc: "부서·주제별 사내 소통 게시판 (댓글·좋아요·첨부 미리보기)" },
            { name: "결재", desc: "전자결재 상신·승인 (결재선 지정, 받은/올린 결재)" },
            { name: "메일", desc: "사내 메일 송·수신 (예약·단체 발송, 과업별 폴더 분류, 첨부 미리보기)" },
            { name: "사내 연락처", desc: "내 연락처·공용 연락처 등록 및 공유, 사내 전체 직원 명부 조회" },
            { name: "사내 일정", desc: "개인·부서 일정 캘린더" },
        ],
    },
    {
        group: "사내 활동",
        items: [
            { name: "비품 예약", desc: "사내 비품 등록 및 예약 (카테고리·위치 관리)" },
            { name: "회의록", desc: "부서별 주간 회의록 작성 및 버전 이력 관리" },
            { name: "차계부", desc: "법인차량 운행일지 — 차량별 월간 기록, 주행거리·주유·특이사항" },
            { name: "피드백", desc: "요청·건의·개선사항 게시판 (댓글·좋아요)" },
        ],
    },
    {
        group: "행사 운영",
        items: [
            { name: "행사", desc: "키비주얼(2D)·3D·행사장 자료 업로드 및 열람" },
            { name: "LED 포토존", desc: "행사·참가자 관리, RFID 출석체크 (권한 보유 시)" },
        ],
    },
    {
        group: "입찰",
        items: [
            { name: "나라장터 공고", desc: "나라장터 입찰 공고 검색·조회" },
            { name: "제안서", desc: "제안서 목록 관리 및 업로드" },
        ],
    },
    {
        group: "별도 운영 (권한 보유 시)",
        items: [
            { name: "재무", desc: "자금 현황·현금흐름표·자금일보, 고정비 달력·긴급 지출 보고서, 미수금·거래처·예산, 지출확정·지출요청" },
            { name: "인사팀", desc: "근태·휴가, 급여, 직원 및 조직(부서·직위·직책) 관리" },
        ],
    },
];

export default function LoginAnnouncementModal({ open, onClose }) {
    const feedbackTotal = FEEDBACK_DONE.length + FEEDBACK_PENDING.length;
    const history = useHistory();
    const [pushSetupOpen, setPushSetupOpen] = React.useState(false);

    const goFeedback = () => {
        onClose?.();
        history.push("/feedback");
    };

    return (
        <>
        <Dialog
            open={Boolean(open)}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: "88vh",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 24px 70px rgba(15,23,42,0.18)",
                },
            }}
        >
            {/* ─── 헤더 (브랜드 그라데이션 배너) ─── */}
            <Box
                sx={{
                    position: "relative",
                    px: 3,
                    py: 2.25,
                    color: "dark",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.75,
                }}
            >
                <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.3, letterSpacing: "-0.3px" }}>
                    온앤미 통합 시스템 안내
                </Typography>
            </Box>


            {/* ─── 본문 (스크롤) ─── */}
            <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5, bgcolor: "#fff" }}>
                {/* 인사말 */}
                <Typography sx={{ fontSize: 14, color: ds.colors.textSecondary, lineHeight: 1.7, mb: 2.25 }}>
                    안녕하세요. 사내 업무를 한곳에서 처리할 수 있도록 <b>온앤미 통합 시스템</b>을 운영하고 있습니다. <br/>
                    기존 메일플러그, 플로우에 흩어져 있던 기능들을 하나로 통합하고, 보다 효율적으로 업무를 진행하기 위해 해당 시스템이 개발되었습니다.
                    <br/> <br/>
                    <b>먼저 귀한 시간을 내어 피드백을 남겨주신 모든 분들께 진심으로 감사드립니다.</b> <br/>
                    남겨주신 의견 하나하나가 시스템을 다듬는 데 큰 도움이 되고 있으며, 우선순위를 정해 순차적으로 반영하고 있습니다.
                </Typography>


                {/* ─── 신규: 휴대폰·PC 실시간 알림 ───────────────────────────
                    여기에 두는 이유 — 셋업은 직원이 <b>한 번</b> 해야 하는 일이고,
                    로그인 직후가 유일하게 전 직원이 반드시 지나가는 지점이다.
                    별도 공지 메일이나 교육을 돌리지 않아도 안내가 도달한다. */}
                <SectionLabel icon={<PhoneIphoneRoundedIcon sx={{ fontSize: 16 }} />}>
                    새 기능 — 휴대폰·PC 실시간 알림
                </SectionLabel>
                <Box sx={{ border: "1px solid #fecaca", bgcolor: "#fff7f7", borderRadius: 1.5,
                           px: 2, py: 1.75, mb: 2.5 }}>
                    <Typography sx={{ fontSize: 13, color: ds.colors.textSecondary, lineHeight: 1.75, mb: 1.25 }}>
                        결재 차례·새 메일·멘션이 생기면 <b>잠금화면으로 바로</b> 알려드립니다.
                        앱을 설치할 필요는 없고, <b>기기마다 한 번만</b> 등록하면 됩니다.
                        <br />
                        알림에는 발신자와 제목만 표시되며, <b>내용 확인은 PC 에서</b> 하시면 됩니다.
                    </Typography>

                    <PushSetupSteps />

                    <ActionButton
                        onClick={() => setPushSetupOpen(true)}
                        startIcon={<PhoneIphoneRoundedIcon />}
                        sx={{ mt: 1.5 }}
                    >
                        지금 등록하기
                    </ActionButton>
                    <Typography sx={{ fontSize: 11.5, color: ds.colors.textDisabled, mt: 1 }}>
                        나중에 하시려면 좌측 프로필의 종 아이콘 → <b>실시간 알림</b> 에서 언제든 켤 수 있습니다.
                    </Typography>
                </Box>

                {/* 피드백 반영 현황 */}
                <SectionLabel>피드백 반영 현황</SectionLabel>
                <Typography sx={{ fontSize: 12.5, color: ds.colors.textSecondary, mb: 1.25, lineHeight: 1.6 }}>
                    주신 피드백 <b>총 {feedbackTotal}건</b> 중 <b>{FEEDBACK_DONE.length}건</b>은 반영이 완료되었고,
                    나머지 <b>{FEEDBACK_PENDING.length}건</b>은 진행 중이거나 아래 일정으로 예정되어 있습니다.
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 1.5,
                        mb: 2.75,
                    }}
                >
                    {/* 반영 완료 */}
                    <FeedbackColumn
                        title="반영 완료"
                        count={FEEDBACK_DONE.length}
                        color={ds.colors.success}
                        bg={ds.colors.successBg}
                        icon={<CheckCircleRoundedIcon sx={{ fontSize: 16 }} />}
                    >
                        {FEEDBACK_DONE.map((f, i) => (
                            <FeedbackItem key={f.name} no={i + 1} name={f.name} desc={f.desc} color={ds.colors.success} />
                        ))}
                    </FeedbackColumn>

                    {/* 진행 중 · 예정 */}
                    <FeedbackColumn
                        title="진행 중 · 예정"
                        count={FEEDBACK_PENDING.length}
                        color={ds.colors.warning}
                        bg={ds.colors.warningBg}
                        icon={<ScheduleRoundedIcon sx={{ fontSize: 16 }} />}
                    >
                        {FEEDBACK_PENDING.map((f, i) => (
                            <FeedbackItem
                                key={f.name}
                                no={i + 1}
                                name={f.name}
                                desc={f.desc}
                                eta={f.eta}
                                color={ds.colors.warning}
                            />
                        ))}
                    </FeedbackColumn>
                </Box>

                {/* 기능 개요 표 */}
                <Typography sx={{ fontSize: 14, color: ds.colors.textSecondary, lineHeight: 1.7, mb: 2.25 }}>
                    현재는 좌측 사이드바 메뉴를 통해 아래 기능들을 이용하실 수 있습니다.
                </Typography>
                <SectionLabel>제공 기능</SectionLabel>
                <Box
                    sx={{
                        border: `1px solid ${ds.colors.border}`,
                        borderRadius: 1.5,
                        overflow: "hidden",
                        mb: 2.75,
                    }}
                >
                    <Table size="small" sx={{ tableLayout: "fixed" }}>
                        {/* tableLayout:fixed 는 컬럼 폭을 첫 행이 아닌 <col> 에서 가져가므로,
                            그룹 헤더(colSpan=2)가 첫 행이어도 이름/설명 폭이 26%/나머지로 고정된다. */}
                        <colgroup>
                            <col style={{ width: "26%" }} />
                            <col />
                        </colgroup>
                        <TableBody>
                            {FEATURE_GROUPS.map((g) => (
                                <React.Fragment key={g.group}>
                                    {/* 그룹 헤더 밴드 */}
                                    <TableRow>
                                        <TableCell
                                            colSpan={2}
                                            sx={{
                                                bgcolor: ds.colors.primaryBg,
                                                borderBottom: `1px solid ${ds.colors.border}`,
                                                py: 0.85,
                                                px: 1.75,
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.85, color: ds.colors.primaryText }}>
                                                {g.icon}
                                                <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: "-0.2px" }}>
                                                    {g.group}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {/* 항목들 */}
                                    {g.items.map((it) => (
                                        <TableRow key={it.name} sx={{ "&:hover": { bgcolor: ds.colors.rowHover } }}>
                                            <TableCell
                                                sx={{
                                                    width: "26%",
                                                    verticalAlign: "top",
                                                    py: 1,
                                                    px: 1.75,
                                                    borderBottom: `1px solid ${ds.colors.borderLight}`,
                                                    fontSize: 13,
                                                    fontWeight: 700,
                                                    color: ds.colors.textPrimary,
                                                }}
                                            >
                                                {it.name}
                                                {it.badge && (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            ml: 0.6,
                                                            px: 0.6,
                                                            py: 0.1,
                                                            fontSize: 10.5,
                                                            fontWeight: 800,
                                                            whiteSpace: "nowrap",
                                                            color: ds.colors.warning,
                                                            bgcolor: ds.colors.warningBg,
                                                            border: `1px solid ${ds.colors.warning}`,
                                                            borderRadius: 999,
                                                        }}
                                                    >
                                                        {it.badge}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    verticalAlign: "top",
                                                    py: 1,
                                                    px: 1.75,
                                                    borderBottom: `1px solid ${ds.colors.borderLight}`,
                                                    fontSize: 12.5,
                                                    color: ds.colors.textSecondary,
                                                    lineHeight: 1.55,
                                                }}
                                            >
                                                {it.desc}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                {/* 피드백 안내 콜아웃 */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        borderRadius: 1.5,
                        p: 1.75,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: ds.colors.primaryText, lineHeight: 1.5 }}>
                            그 외 요청·건의·개선 사항이 있으실 경우 남겨주세요.
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: ds.colors.textSecondary, mt: 0.25, lineHeight: 1.55 }}>
                            좌측 사이드바의 <b>‘피드백’</b> 메뉴에서 자유롭게 남겨주시면 검토 후 반영하겠습니다.
                        </Typography>
                    </Box>
                    <ActionButton
                        variant="secondary"
                        onClick={goFeedback}
                        startIcon={<ChevronRightRoundedIcon sx={{ fontSize: 16 }} />}
                        label="피드백 남기러 가기"
                        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                    />
                </Box>
            </Box>

            {/* ─── 푸터 ─── */}
            <Box
                sx={{
                    px: 3,
                    py: 1.5,
                    borderTop: `1px solid ${ds.colors.border}`,
                    bgcolor: "#fafbfc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    flexShrink: 0,
                }}
            >
                <ActionButton variant="primary" onClick={onClose} label="확인하고 시작하기" sx={{ minWidth: 128 }} />
            </Box>
        </Dialog>

        {/* 공지 모달 위에 겹쳐 띄운다 — 공지를 닫지 않는 이유는, 등록을 마친 뒤 남은 안내를
            이어서 읽게 하려는 것이다(닫아 버리면 로그인당 1회라 다시 볼 수 없다). */}
        <PushSetupModal open={pushSetupOpen} onClose={() => setPushSetupOpen(false)} />
        </>
    );
}

/* ─── 피드백 현황 컬럼 (완료 / 진행 중·예정) ─── */
function FeedbackColumn({ title, count, color, bg, icon, children }) {
    return (
        <Box
            sx={{
                border: `1px solid ${ds.colors.border}`,
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.5,
                    py: 0.9,
                    bgcolor: bg,
                    borderBottom: `1px solid ${ds.colors.border}`,
                    color,
                }}
            >
                {icon}
                <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: "-0.2px" }}>{title}</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 800 }}>{count}건</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", p: 1.25, gap: 1 }}>{children}</Box>
        </Box>
    );
}

/* ─── 피드백 현황 항목 한 줄 ─── */
function FeedbackItem({ no, name, desc, eta, color }) {
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color, lineHeight: 1.6, flexShrink: 0, minWidth: 14 }}>
                {no}.
            </Typography>
            <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: ds.colors.textPrimary, lineHeight: 1.5 }}>
                        {name}
                    </Typography>
                    {eta && (
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color,
                                border: `1px solid ${color}`,
                                borderRadius: 999,
                                px: 0.75,
                                py: 0.05,
                                lineHeight: 1.5,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {eta}
                        </Typography>
                    )}
                </Box>
                <Typography sx={{ fontSize: 12.5, color: ds.colors.textSecondary, lineHeight: 1.55 }}>{desc}</Typography>
            </Box>
        </Box>
    );
}

/* ─── 섹션 라벨 (본문 소제목) ─── */
/**
 * 셋업 절차 — 지금 보고 있는 기기에 맞는 것만 보여준다.
 *
 * <p>세 플랫폼 절차를 한꺼번에 늘어놓으면 <b>자기에게 해당되는 줄을 찾다가 포기한다.</b>
 * 특히 iOS 는 4단계고 순서를 하나만 틀려도 실패하므로, 다른 플랫폼 안내와 섞이면 안 된다.
 *
 * <p>PC 에서 보는 사람이 대다수라(로그인 직후다) PC 절차를 기본으로 두고, 폰 절차는
 * "QR 로 이어서" 한 줄로 요약한다 — 실제 폰 안내는 QR 을 찍고 들어간 화면
 * ({@code PhoneSetupPage}) 이 기기에 맞춰 다시 해 준다. 여기서 중복 설명하지 않는다.
 */
function PushSetupSteps() {
    const iosLike = isIOS();

    if (iosLike && !isStandalone()) {
        // 아이폰·아이패드에서 이 공지를 보는 경우 — 사파리 탭 상태다.
        return (
            <StepList
                title="이 아이폰에서 켜려면"
                steps={[
                    "하단 공유 버튼 → 홈 화면에 추가",
                    "홈 화면에 생긴 온앤미 아이콘으로 앱 열기",
                    "로그인 (그 아이콘에서 처음 한 번만)",
                    "이 안내 창의 [지금 등록하기] → 이 기기에서 받기 켜기",
                ]}
                note="애플 제약으로 홈 화면에 추가한 뒤에만 알림을 켤 수 있습니다. 홈 화면 앱은 Safari 와 로그인이 분리되어 한 번 더 로그인합니다."
            />
        );
    }

    return (
        <StepList
            title="등록 방법"
            steps={[
                `이 ${isAndroid() ? "폰" : "PC"} — 아래 버튼 → 토글 ON → 권한 [허용] (5초)`,
                "휴대폰 — 같은 화면의 [QR 코드 만들기] 를 폰 카메라로 촬영",
            ]}
            note="아이폰은 QR 을 Safari 로 찍어야 하며, 홈 화면에 추가하는 단계가 한 번 더 있습니다."
        />
    );
}

function StepList({ title, steps, note }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: ds.colors.textPrimary, mb: 0.75 }}>
                {title}
            </Typography>
            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                {steps.map((s) => (
                    <Box component="li" key={s}
                         sx={{ fontSize: 12.5, color: ds.colors.textSecondary, lineHeight: 1.8 }}>
                        {s}
                    </Box>
                ))}
            </Box>
            {note && (
                <Typography sx={{ fontSize: 11.5, color: ds.colors.textDisabled, mt: 0.75 }}>
                    {note}
                </Typography>
            )}
        </Box>
    );
}

function SectionLabel({ children, icon }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1, color: ds.colors.textPrimary }}>
            {icon}
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, letterSpacing: "-0.2px" }}>{children}</Typography>
        </Box>
    );
}
